// src/pages/Admin/Dashboard/Events/components/EventDetails.jsx

import React from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaCommentDots,
  FaEnvelope,
  FaExternalLinkAlt,
  FaImage,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPen,
  FaPhone,
  FaTag,
  FaTimes,
  FaToggleOn,
  FaUser,
  FaUserPlus,
} from "react-icons/fa";

import {
  formatEventDate,
  formatRegistrationDate,
  getMediaUrl,
  getRegistrationsCount,
  getSafeRegistrations,
} from "../utils/eventsUtils";

function DetailItem({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        {Icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Icon />
          </span>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

function RegistrationsList({ registrations = [] }) {
  if (!registrations.length) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white text-slate-400 dark:bg-slate-950">
          <FaUserPlus />
        </div>

        <h5 className="mt-4 text-base font-bold text-slate-950 dark:text-white">
          Aucun inscrit pour le moment
        </h5>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Les inscriptions réalisées depuis la page publique apparaîtront ici.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {registrations.map((registration, index) => (
        <article
          key={
            registration.id ||
            `${registration.nom}-${registration.prenom}-${index}`
          }
          className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-white text-blue-600 dark:bg-slate-950 dark:text-blue-300">
                  <FaUser />
                </span>

                <div>
                  <h5 className="text-base font-bold text-slate-950 dark:text-white">
                    {registration.prenom || "Prénom"}{" "}
                    {registration.nom || "Nom"}
                  </h5>

                  <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    Inscrit le{" "}
                    {formatRegistrationDate(registration.date_inscription)}
                  </p>
                </div>
              </div>
            </div>

            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
              #{index + 1}
            </span>
          </div>

          <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            {registration.email && (
              <div className="flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                <FaEnvelope className="text-slate-400" />
                <span className="break-all">{registration.email}</span>
              </div>
            )}

            {registration.telephone && (
              <div className="flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                <FaPhone className="text-slate-400" />
                <span>{registration.telephone}</span>
              </div>
            )}

            {registration.commentaire && (
              <div className="flex items-start gap-2 rounded-2xl bg-white px-3 py-2 text-slate-600 dark:bg-slate-950 dark:text-slate-300 sm:col-span-2">
                <FaCommentDots className="mt-1 text-slate-400" />
                <span className="whitespace-pre-line">
                  {registration.commentaire}
                </span>
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

export default function EventDetails({ event, onEdit, onClose }) {
  const imageUrl = getMediaUrl(event.photo_url);
  const registrationsCount = getRegistrationsCount(event);
  const registrations = getSafeRegistrations(event.registrations_json);

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="relative h-72 bg-slate-100 dark:bg-slate-800">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={event.titre}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-slate-400">
              <FaImage className="text-5xl" />
              <p className="mt-3 text-sm font-bold">Aucune image renseignée</p>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-5">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-200">
              Événement
            </p>
            <h3 className="mt-1 text-3xl font-bold text-white">
              {event.titre}
            </h3>
          </div>
        </div>

        <div className="p-5">
          <p className="text-base font-semibold leading-7 text-slate-700 dark:text-slate-200">
            {event.description || "Description non renseignée."}
          </p>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Informations
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem
            label="Date de début"
            value={formatEventDate(event.start)}
            icon={FaCalendarAlt}
          />

          <DetailItem
            label="Date de fin"
            value={formatEventDate(event.end)}
            icon={FaClock}
          />

          <DetailItem label="Type" value={event.type_event} icon={FaTag} />

          <DetailItem
            label="Catégorie"
            value={event.cate_event}
            icon={FaInfoCircle}
          />

          <DetailItem
            label="Niveau"
            value={event.level_event}
            icon={FaInfoCircle}
          />

          <DetailItem
            label="Lieu"
            value={event.lieu_event}
            icon={FaMapMarkerAlt}
          />

          <DetailItem
            label="Événement actif"
            value={event.is_active ? "Oui" : "Non"}
            icon={FaToggleOn}
          />

          <DetailItem
            label="Inscriptions ouvertes"
            value={event.can_register ? "Oui" : "Non"}
            icon={FaUserPlus}
          />

          <DetailItem
            label="Inscrits"
            value={`${registrationsCount} inscrit${
              registrationsCount > 1 ? "s" : ""
            }`}
            icon={FaUserPlus}
          />

          <DetailItem
            label="Maximum inscriptions"
            value={event.max_registrations || "Illimité"}
            icon={FaUserPlus}
          />

          <div className="sm:col-span-2">
            <DetailItem
              label="Image"
              value={event.photo_url || "Non renseignée"}
              icon={FaImage}
            />
          </div>

          <div className="sm:col-span-2">
            <DetailItem
              label="Lien agenda externe"
              value={event.agenda_url || "Non renseigné"}
              icon={FaExternalLinkAlt}
            />
          </div>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Horaires détaillés
        </h4>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
          <p className="whitespace-pre-line text-sm leading-7 text-slate-700 dark:text-slate-200">
            {event.horaire_event || "Aucun horaire détaillé renseigné."}
          </p>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
            Liste des inscrits
          </h4>

          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
            <FaUserPlus />
            {registrations.length} inscrit{registrations.length > 1 ? "s" : ""}
          </span>
        </div>

        <RegistrationsList registrations={registrations} />
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaPen />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
}
